import { mimeTypes } from 'client/constants/editorSettings'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SeoSettingsFields } from 'src/editor/api/website-pages-api'
import { ChevronDownIcon } from 'src/editor/shared/icons/chevron-down-icon'
import { ChevronUpIcon } from 'src/editor/shared/icons/chevron-up-icon'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { ButtonSizeEnum, TextButton } from '../../buttons'
import { Toggle } from '../../toggle'
import { FileUploader } from '../../uploader/file-uploader'

interface SeoSettingsTabProps {
  urlPath?: string
  websiteDomainName?: string
  updateField: (
    field: keyof SeoSettingsFields,
  ) => (val: SeoSettingsFields[keyof SeoSettingsFields]) => void
  fields: SeoSettingsFields
}

export const SeoSettingsTab = ({
  urlPath = '',
  websiteDomainName,
  updateField,
  fields: { title, description, author, socialImage, keywords, index },
}: SeoSettingsTabProps) => {
  const { t } = useTranslation()

  const [isPreviewActive, setIsPreviewActive] = useState(false)
  const [isImageUploading, setIsImageUploading] = useState(false)

  return (
    <div className="flex max-h-[618px] flex-col gap-4 overflow-y-auto">
      <span className="text-heading-4">
        {t('website_settings.sidebar.page_settings.seo')}
      </span>
      <p className="text-M text-textTertiary">
        {t('website_settings.sidebar.page_settings.seo_info')}
      </p>
      <TextInputControl
        wrapperProps={{
          className: 'flex mt-0',
          labelText: t('title'),
        }}
        value={title}
        update={updateField('title')}
      />
      <TextInputControl
        wrapperProps={{
          className: 'flex mt-0',
          labelText: t('description'),
        }}
        value={description}
        update={updateField('description')}
      />
      <TextInputControl
        wrapperProps={{
          className: 'flex mt-0',
          labelText: t('page_settings.seo.keywords.label'),
        }}
        value={keywords}
        update={updateField('keywords')}
      />
      <TextInputControl
        wrapperProps={{
          className: 'flex mt-0',
          labelText: t('page_settings.seo.author.label'),
        }}
        value={author}
        update={updateField('author')}
      />
      <TextButton
        className="justify-start"
        size={ButtonSizeEnum.Medium}
        onClick={() => setIsPreviewActive(prev => !prev)}
        title={t('core.header.pop_up.title')}
        active={isPreviewActive}
        iconBefore={isPreviewActive ? <ChevronUpIcon /> : <ChevronDownIcon />}
      >
        {t('website_settings.sidebar.page_settings.seo_search_preview_label')}
      </TextButton>
      {isPreviewActive && (
        <div className="flex flex-col rounded-md border border-border200 p-6">
          <span className="mb-1.5 text-base text-textLink">{title}</span>
          <span className="text-textTertiary text-xs">
            {`https://${websiteDomainName || 'domain.placeholder.com'}/${urlPath}`}
          </span>
          {!!description && (
            <>
              <span className="my-3 h-[1px] w-full bg-divider01"></span>
              <span>{description}</span>
            </>
          )}
        </div>
      )}
      <span className="text-heading-4">
        {t('website_settings.sidebar.page_settings.seo_image')}
      </span>
      <FileUploader
        mimeTypes={mimeTypes.image}
        show
        isSingleFileMode
        afterUpload={updateField('socialImage')}
        onUploading={setIsImageUploading}
        uploading={isImageUploading}
        removeFile={() => {
          updateField('socialImage')(undefined)
        }}
        userFiles={
          typeof socialImage === 'object' && socialImage ? [socialImage] : []
        }
        shouldOptimize={false}
      />
      <Toggle
        label={'page_settings.seo.search_engines.label'}
        onChange={updateField('index')}
        checked={index}
      />
    </div>
  )
}
